/*
 * minimum-quantity.js
 * Frontend enforcement for client-configured minimum order quantities.
 *
 * Data comes from Liquid:
 *   - PDP : the product-form quantity input carries min="<effective min>".
 *   - Cart: each line quantity input carries data-minimum-quantity="<effective min>".
 *
 * Enforcement points:
 *   1. PDP  — reactively disable purchase buttons + show a note while qty < min,
 *             and hard-block the add-to-cart form submit.
 *   2. Cart — universal backstop: block every checkout button + show a per-line note
 *             while ANY line's qty is below its minimum (also catches quick-adds).
 *
 * NOTE: This is a storefront (frontend) guard. It is not a substitute for server-side
 *       (checkout Function / order-limit app) enforcement against direct cart-API calls.
 */
(function () {
  'use strict';

  function toInt(v) {
    var n = parseInt(v, 10);
    return isNaN(n) ? 0 : n;
  }

  var CONFIG = (function () {
    try {
      return JSON.parse(document.getElementById('minimum-quantity-config').textContent);
    } catch (e) {
      return {};
    }
  })();

  function msg(min) {
    var tpl = (CONFIG && CONFIG.message) || 'A minimum of [min] is required for this item.';
    return String(tpl).replace(/\[min\]/g, min);
  }

  // Insert / update / remove a note directly after `anchor`.
  function setNote(anchor, text) {
    if (!anchor) return;
    var next = anchor.nextElementSibling;
    var note = next && next.classList && next.classList.contains('minimum-quantity-note') ? next : null;
    if (text) {
      if (!note) {
        note = document.createElement('div');
        note.className = 'minimum-quantity-note';
        note.setAttribute('role', 'alert');
        anchor.parentNode.insertBefore(note, anchor.nextSibling);
      }
      note.textContent = text;
    } else if (note) {
      note.remove();
    }
  }

  /* ======================================================================
   * 1. PRODUCT PAGE
   * ==================================================================== */
  function pdpInputs() {
    return document.querySelectorAll(
      'product-form form[action*="/cart/add"] input[name="quantity"]'
    );
  }

  function validatePdpInput(input) {
    var min = toInt(input.getAttribute('min')) || toInt(input.getAttribute('data-min'));
    var val = toInt(input.value);
    var invalid = min > 1 && val > 0 && val < min;
    var pf = input.closest('product-form');

    if (pf) {
      var submit = pf.querySelector('[type="submit"]');
      if (submit) {
        submit.classList.toggle('disabled', invalid);
        submit.setAttribute('aria-disabled', invalid ? 'true' : 'false');
      }
      var dyn = pf.querySelector('.shopify-payment-button');
      if (dyn) {
        dyn.style.pointerEvents = invalid ? 'none' : '';
        dyn.style.opacity = invalid ? '0.4' : '';
      }
    }
    setNote(input.closest('.product-form__quantity') || input.closest('quantity-input'), invalid ? msg(min) : '');
    return !invalid;
  }

  function showFormError(form, text) {
    var wrap = form.querySelector('.product-form__error-message-wrapper');
    var el = wrap && wrap.querySelector('.product-form__error-message');
    if (wrap && el) {
      el.textContent = text;
      wrap.removeAttribute('hidden');
    } else {
      var pf = form.closest('product-form');
      setNote((pf && pf.querySelector('.product-form__quantity')) || form, text);
    }
  }

  function initPdp() {
    pdpInputs().forEach(function (input) {
      if (!input.dataset.mqBound) {
        input.dataset.mqBound = '1';
        input.addEventListener('change', function () { validatePdpInput(input); });
        input.addEventListener('input', function () { validatePdpInput(input); });
      }
      validatePdpInput(input);
    });
  }

  // Hard block: if the add-to-cart form is submitted below min, snap up and stop.
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      var action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;
      var input = form.querySelector('input[name="quantity"]');
      if (!input) return; // quick-add without a qty field → cart backstop handles it
      var min = toInt(input.getAttribute('min')) || toInt(input.getAttribute('data-min'));
      var val = toInt(input.value);
      if (min > 1 && val < min) {
        e.preventDefault();
        e.stopImmediatePropagation();
        input.value = min;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        showFormError(form, msg(min));
      }
    },
    true
  );

  /* ======================================================================
   * 2. CART (universal backstop)
   * ==================================================================== */
  var CHECKOUT_SEL = '#checkout, #CartDrawer-Checkout, button[name="checkout"], .cart__checkout-button';

  function validateCart() {
    var blocked = false;
    document.querySelectorAll('input[data-minimum-quantity]').forEach(function (input) {
      var min = toInt(input.getAttribute('data-minimum-quantity'));
      var val = toInt(input.value);
      var invalid = min > 1 && val > 0 && val < min;
      setNote(input.closest('quantity-input'), invalid ? msg(min) : '');
      if (invalid) blocked = true;
    });

    document.querySelectorAll(CHECKOUT_SEL).forEach(function (btn) {
      if (blocked) {
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('disabled');
        btn.dataset.mqBlocked = '1';
      } else if (btn.dataset.mqBlocked) {
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('disabled');
        delete btn.dataset.mqBlocked;
      }
    });

    document.querySelectorAll('.additional-checkout-buttons, .cart__dynamic-checkout-buttons').forEach(function (c) {
      c.style.pointerEvents = blocked ? 'none' : '';
      c.style.opacity = blocked ? '0.4' : '';
    });

    return !blocked;
  }

  // Block checkout click while any line is below its minimum.
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest ? e.target.closest(CHECKOUT_SEL) : null;
      if (btn && btn.dataset.mqBlocked) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
    true
  );

  // Block cart-form submit (Enter key / native submit) while invalid.
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.querySelector('input[data-minimum-quantity]')) return; // only the cart form
      if (!validateCart()) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
    true
  );

  /* ======================================================================
   * Wiring — run on load, on cart re-render, and on any qty change.
   * ==================================================================== */
  function runAll() {
    initPdp();
    validateCart();
  }
  var schedule = function () { setTimeout(runAll, 60); };

  document.addEventListener('DOMContentLoaded', runAll);
  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('input[data-minimum-quantity]')) schedule();
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    if (PUB_SUB_EVENTS.cartUpdate) subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
    if (PUB_SUB_EVENTS.quantityUpdate) subscribe(PUB_SUB_EVENTS.quantityUpdate, schedule);
    if (PUB_SUB_EVENTS.variantChange) subscribe(PUB_SUB_EVENTS.variantChange, schedule);
  }

  // Deferred script: DOMContentLoaded may have already fired.
  runAll();
})();
